import { TransformUtils } from '@inter-ikea-core/core';
import { ComponentIds, HierarchyUtils, MeasurementUtils, Radians, Vector } from '@inter-ikea-core/core';
import { LineMeasureWidget } from '@inter-ikea-betrakta/viewer/dist/modules/MeasurementWidget';
import { EventComponent, FrameGroupComponent, OuterMeasurementsComponent, PlannerEcsMultiSystem, ProductInfoComponent, ProductType, eulerDegreesToRadians, getDistanceBetweenPoints, getLabelPositions, isFacingCamera, rotateStartAndEndVectorsRoundCenter } from '@inter-ikea-storageone/range';
import MeasurementsLabelComponent from '@inter-ikea-storageone/range/src/core-api/components/MeasurmentsLabelComponent';
export class PaxMeasurementsSystem extends PlannerEcsMultiSystem {
    constructor(coreApi) {
        super(coreApi, 'PaxMeasurementsSystem', [
            ComponentIds.IS_DRAGGED_COMPONENT_ID,
            FrameGroupComponent.id,
            EventComponent.id,
            ComponentIds.IS_LOADING_SNAPSHOT_COMPONENT_ID,
            ComponentIds.IN_SCENE_COMPONENT_ID
        ]);
        this.measurements = [];
        this.visible = true;
        this.LINE_COLOR = { r: 0.1, g: 0.1, b: 0.1 };
        this.LINE_THICKNESS = 0.007;
        this.CAP_LENGTH = 0.02;
        this.MEASUREMENT_PADDING = 80;
        this.PADDING_BETWEEN_FRAMES = 2; // We take account for frames not mounting completely flush and add 2 mm between frames
        this.getSize = (entity) => {
            if (this.isPartOfCornerSolution(entity) &&
                !this.coreApi.entities.isProductType(entity, ProductType.PAX_ADD_ON_CORNER_FRAME)) {
                const size = entity.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
                return size;
            }
            else {
                const allEntities = HierarchyUtils.getDescendantsWithComponent(entity, OuterMeasurementsComponent.id);
                const transform = TransformUtils.worldTransform(entity);
                const outerMeasurements = MeasurementUtils.getLocalOutsideMeasurementForEntities([entity, ...allEntities], transform);
                return outerMeasurements;
            }
        };
    }
    enable() {
        this.isEnabled = true;
        this.recalculate();
    }
    disable() {
        this.isEnabled = false;
        this.recalculate();
    }
    onAdd(entity, newComponent) {
        switch (newComponent.typeId) {
            case FrameGroupComponent.id:
            case ComponentIds.IN_SCENE_COMPONENT_ID:
                if (entity.hasComponent(OuterMeasurementsComponent.id))
                    this.recalculate();
                break;
            case ComponentIds.IS_DRAGGED_COMPONENT_ID:
                this.visible = false;
                this.recalculate();
                break;
        }
    }
    onUpdate(entity, oldComponent, newComponent) {
        switch (newComponent.typeId) {
            case FrameGroupComponent.id:
                this.recalculate();
                break;
            case EventComponent.id:
                const component = newComponent;
                const oldComp = oldComponent;
                // When moving camera we need to update labels to correct x,y positions.
                // History is needed for updating e.g. when adding doors and other things
                // that are not frames but should be included in the measurements
                if (oldComp.event === 'update' && component.event === 'update') {
                    this.recalculate();
                }
                break;
        }
    }
    onRemove(entity, oldComponent) {
        if (oldComponent.typeId === FrameGroupComponent.id ||
            oldComponent.typeId === ComponentIds.IS_LOADING_SNAPSHOT_COMPONENT_ID) {
            this.recalculate();
        }
        else if (oldComponent.typeId === ComponentIds.IS_DRAGGED_COMPONENT_ID) {
            this.visible = true;
            this.recalculate();
        }
        else if (oldComponent.typeId === ComponentIds.IN_SCENE_COMPONENT_ID) {
            if (entity.hasComponent(OuterMeasurementsComponent.id)) {
                this.recalculate();
            }
        }
    }
    recalculate() {
        this.measurements.forEach(m => m.lines.forEach(l => l.dispose && l.dispose()));
        this.measurements = [];
        if (this.isEnabled) {
            this.createMeasurementLines();
            this.showHideMeasurementsFacingCamera();
            this.setLabels();
        }
    }
    createMeasurementLines() {
        if (!this.visible)
            return [];
        const allFrameGroups = this.coreApi.entities.getByComponent(FrameGroupComponent.id);
        // Loop all frame groups
        allFrameGroups.forEach(frameGroupEntity => {
            const component = frameGroupEntity.getComponent(FrameGroupComponent.id);
            const groupFrameEntities = component?.frames;
            if (groupFrameEntities && groupFrameEntities.length > 0) {
                const groupCenter = this.getCenter(groupFrameEntities);
                const groupOuterMeasurements = this.getOutsideMeasurements(groupFrameEntities);
                if (groupFrameEntities[0].isDisposed)
                    return;
                const rotation = this.coreApi.entities
                    .getWorldRotation(groupFrameEntities[0])
                    .toEulerDegrees();
                if (!rotation)
                    return;
                if (groupOuterMeasurements) {
                    const measurement = { lines: [], rotation };
                    measurement.lines.push(this.getHeightMeasurementLine(groupOuterMeasurements, groupCenter, rotation, true));
                    measurement.lines.push(this.getWidthMeasurementLine(groupOuterMeasurements, groupCenter, rotation, true));
                    measurement.lines.push(this.getDepthMeasurementLine(groupOuterMeasurements, groupCenter, rotation));
                    this.measurements.push(measurement);
                }
            }
        });
    }
    setLabels() {
        const allMeasurementLines = this.measurements
            .filter(m => m.lines[0].visible)
            .flatMap(m => m.lines.map(l => ({ start: l.start, end: l.end })));
        const labelPositions = getLabelPositions(this.coreApi, allMeasurementLines, {
            x: 0,
            y: 0,
            z: 0
        });
        const scene = this.coreApi.entities.getScene();
        if (scene) {
            scene.removeComponent(MeasurementsLabelComponent.id);
            scene.addComponent(new MeasurementsLabelComponent(labelPositions));
        }
    }
    invertVal(val, val2, invert) {
        return invert ? val - val2 : val + val2;
    }
    calculateMeasurementClosestToCamera(m1, m2) {
        const cameraPosition = this.coreApi.ipexViewer.get_active_camera()?.position;
        if (!cameraPosition) {
            throw new Error('No camera found');
        }
        const cameraPosVector = this.v32v(cameraPosition);
        const distanceToM1 = getDistanceBetweenPoints(cameraPosVector, m1);
        const distanceToM2 = getDistanceBetweenPoints(cameraPosVector, m2);
        return distanceToM1 <= distanceToM2;
    }
    getHeightMeasurementLine(groupOuterMeasurements, groupCenter, rotation, wallAligned) {
        const leftStart = new Vector(groupCenter.X - groupOuterMeasurements.width / 2 - this.MEASUREMENT_PADDING, groupCenter.Y - groupOuterMeasurements.height / 2, this.invertVal(groupCenter.Z, groupOuterMeasurements.depth / 2, !!wallAligned));
        const leftEnd = new Vector(groupCenter.X - groupOuterMeasurements.width / 2 - this.MEASUREMENT_PADDING, groupCenter.Y + groupOuterMeasurements.height / 2, this.invertVal(groupCenter.Z, groupOuterMeasurements.depth / 2, !!wallAligned));
        const rightStart = new Vector(groupCenter.X + groupOuterMeasurements.width / 2 + this.MEASUREMENT_PADDING, groupCenter.Y - groupOuterMeasurements.height / 2, this.invertVal(groupCenter.Z, groupOuterMeasurements.depth / 2, !!wallAligned));
        const rightEnd = new Vector(groupCenter.X + groupOuterMeasurements.width / 2 + this.MEASUREMENT_PADDING, groupCenter.Y + groupOuterMeasurements.height / 2, this.invertVal(groupCenter.Z, groupOuterMeasurements.depth / 2, !!wallAligned));
        const { rotatedStart: rotatedLeftStart, rotatedEnd: rotatedLeftEnd } = rotateStartAndEndVectorsRoundCenter(groupCenter, leftStart, leftEnd, rotation.Y);
        const { rotatedStart: rotatedRightStart, rotatedEnd: rotatedRightEnd } = rotateStartAndEndVectorsRoundCenter(groupCenter, rightStart, rightEnd, rotation.Y);
        const useLeftMeasurements = this.calculateMeasurementClosestToCamera(rotatedLeftStart, rotatedRightStart);
        return this.createMeasurementLine(useLeftMeasurements ? rotatedLeftStart : rotatedRightStart, useLeftMeasurements ? rotatedLeftEnd : rotatedRightEnd, this.getCapDirection(new Vector(1, 0, 0), rotation));
    }
    getWidthMeasurementLine(groupOuterMeasurements, groupCenter, rotation, wallAligned) {
        const start = new Vector(groupCenter.X - groupOuterMeasurements.width / 2, groupCenter.Y + groupOuterMeasurements.height / 2 + this.MEASUREMENT_PADDING, this.invertVal(groupCenter.Z, groupOuterMeasurements.depth / 2, !!wallAligned));
        const end = new Vector(groupCenter.X + groupOuterMeasurements.width / 2, groupCenter.Y + groupOuterMeasurements.height / 2 + this.MEASUREMENT_PADDING, this.invertVal(groupCenter.Z, groupOuterMeasurements.depth / 2, !!wallAligned));
        const { rotatedStart: rotatedStart, rotatedEnd: rotatedEnd } = rotateStartAndEndVectorsRoundCenter(groupCenter, start, end, rotation.Y);
        return this.createMeasurementLine(rotatedStart, rotatedEnd, { x: 0, y: 1, z: 0 });
    }
    getDepthMeasurementLine(groupOuterMeasurements, groupCenter, rotation) {
        const leftStart = new Vector(groupCenter.X - groupOuterMeasurements.width / 2 - this.MEASUREMENT_PADDING, groupCenter.Y - groupOuterMeasurements.height / 2, groupCenter.Z - groupOuterMeasurements.depth / 2);
        const rightStart = new Vector(groupCenter.X + groupOuterMeasurements.width / 2 + this.MEASUREMENT_PADDING, groupCenter.Y - groupOuterMeasurements.height / 2, groupCenter.Z - groupOuterMeasurements.depth / 2);
        const leftEnd = new Vector(groupCenter.X - groupOuterMeasurements.width / 2 - this.MEASUREMENT_PADDING, groupCenter.Y - groupOuterMeasurements.height / 2, groupCenter.Z + groupOuterMeasurements.depth / 2);
        const rightEnd = new Vector(groupCenter.X + groupOuterMeasurements.width / 2 + this.MEASUREMENT_PADDING, groupCenter.Y - groupOuterMeasurements.height / 2, groupCenter.Z + groupOuterMeasurements.depth / 2);
        const { rotatedStart: rotatedLeftStart, rotatedEnd: rotatedLeftEnd } = rotateStartAndEndVectorsRoundCenter(groupCenter, leftStart, leftEnd, rotation.Y);
        const { rotatedStart: rotatedRightStart, rotatedEnd: rotatedRightEnd } = rotateStartAndEndVectorsRoundCenter(groupCenter, rightStart, rightEnd, rotation.Y);
        const useLeftMeasurements = this.calculateMeasurementClosestToCamera(rotatedLeftStart, rotatedRightStart);
        return this.createMeasurementLine(useLeftMeasurements ? rotatedLeftStart : rotatedRightStart, useLeftMeasurements ? rotatedLeftEnd : rotatedRightEnd, this.getCapDirection(new Vector(1, 0, 0), rotation));
    }
    /**
     * Rotates a cap direction to the rotation provided
     * @param startDirection The cap direction in default orientation
     * @param rotation The rotation to rotate the cap direction. Uses Y value.
     * @returns A rotated cap direction
     */
    getCapDirection(startDirection, rotation) {
        const capDirection = startDirection.rotateAroundAxis(new Vector(0, 1, 0), new Radians(eulerDegreesToRadians(rotation.Y)));
        return { x: capDirection.X, y: capDirection.Y, z: capDirection.Z };
    }
    /**
     * Create a Betrakta measurement line
     * @param start Line start vector / point
     * @param end Line end vector / point
     * @param capDirection The cap direction
     * @param groupCenter The center of the entity group.
     * @param rotation The rotation of the entities in world space
     * @returns A new measurement line widget
     */
    createMeasurementLine(start, end, capDirection) {
        return new LineMeasureWidget(this.coreApi.ipexViewer, this.v2v3(start), this.v2v3(end), this.LINE_COLOR, this.LINE_THICKNESS, capDirection, this.CAP_LENGTH);
    }
    /**
     * Get the outer measurements of a group of entities.
     * @param entities All entities to calculate width, depth and height of.
     * @returns The combines width of the entities, the max height and the max depth.
     */
    getOutsideMeasurements(entities) {
        return entities.reduce((result, entity, i) => {
            const entitySize = this.getSize(entity);
            if (entitySize) {
                result.depth = Math.max(result.depth, entitySize.depth);
                result.height = Math.max(result.height, entitySize.height);
                result.width += entitySize.width + (i > 0 ? this.PADDING_BETWEEN_FRAMES : 0);
            }
            return result;
        }, { width: 0, height: 0, depth: 0 });
    }
    /**
     * Converts a Vector to a IVector3 and divides all values by 1000 for
     * use in Betrakta
     * @param v Vector
     * @returns IVector3
     */
    v2v3(v) {
        return { x: v.X / 1000, y: v.Y / 1000, z: v.Z / 1000 };
    }
    v32v(v) {
        return new Vector(v.x * 1000, v.y * 1000, v.z * 1000);
    }
    /**
     * Calculates the center of the provided entities
     * @param entities A group of entities
     * @returns A vector representing the center point of the provided entities
     */
    getCenter(entities) {
        const handleCornerUnit = (entity) => {
            if (this.coreApi.entities.isProductType(entity, ProductType.PAX_ADD_ON_CORNER_FRAME)) {
                return HierarchyUtils.getDescendantsWithComponent(entity, OuterMeasurementsComponent.id);
            }
            else {
                return [];
            }
        };
        const allEntitiesToMeasure = entities
            .flatMap(e => [
            e,
            this.isPartOfCornerSolution(e)
                ? handleCornerUnit(e)
                : HierarchyUtils.getDescendantsWithComponent(e, OuterMeasurementsComponent.id)
        ])
            .flat();
        const groupOuterMeasurements = MeasurementUtils.getOutsideMeasurementWithoutHierarchies(allEntitiesToMeasure);
        if (groupOuterMeasurements) {
            return groupOuterMeasurements.center;
        }
        return new Vector(0, 0, 0);
    }
    isPartOfCornerSolution(entity) {
        return HierarchyUtils.findInUpwardsHierarchy(entity, e => e.getComponent(ProductInfoComponent.id)?.productType ===
            ProductType.PAX_CORNER_SOLUTION_ASM);
    }
    /**
     * Hide the measurement lines not facing the camera.
     * Show the ones that do.
     */
    showHideMeasurementsFacingCamera() {
        this.measurements.forEach(m => {
            m.lines.forEach(l => (l.visible = isFacingCamera(this.coreApi, m.rotation.Y)));
        });
    }
}
//# sourceMappingURL=PaxMeasurementsSystem.js.map